import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getUserData } from '../../services/API';
import cx from 'classnames';
import useIdleTimer from '../Reader/Phrase/hooks/useIdleTimer';
import { resolveIdleTimeouts } from '../Reader/Phrase/hooks/resolveIdleTimeouts';
import useIsMounted from '../Reader/Phrase/hooks/useIsMounted';
import { parseSkipTranslationTags } from '../../services/util';
import { playMarkupSequence, splitRepeatVariationOnSplitMarkers } from '../../services/playMarkupSequence';
import { useMmcResponsiveIconButtonProps } from './useMmcResponsiveIconProps';
import { useContainerHeightVar } from './mmcContainerScaling';
import { NO_RESPONSE, TriggerActions, triggerDataShallowEqual } from '../../constants/constants';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import { logSessionInfo } from '../../services/logger';
import MmcPromptBar from './MmcPromptBar';
import MmcMediaSection from './MmcMediaSection';
import MmcOptionsSection from './MmcOptionsSection';
import MmcSubmitButton from './MmcSubmitButton';
import {
  IDLE_ENCOURAGEMENT_TIMEOUT,
  IDLE_SUBMIT_TIMEOUT,
  TIME_BETWEEN_OPTIONS,
  HIGHLIGHT_DURATION,
  LOG,
  UI_ELEMENTS,
} from './modularMultipleChoiceConstants';


/**
 * Exact strings passed to speech for readOptionsAloud.
 * Format must match speakWithPromise calls in showTargets [show:options] and playInstructions:
 *   textToSpeak: `"${label}".`
 */
function getReadOptionsAloudPrefetchSegments(optionList) {
  return (optionList || []).map((o) => `"${o.text || o.key}".`);
}

function prefetchReadOptionsAloud(speech, optionList) {
  if (!speech?.prefetch) return;
  const segments = getReadOptionsAloudPrefetchSegments(optionList);
  // readOptionsAloud uses skipTranslation: bilingualMode, so prefetch with same flag for cache alignment
  if (segments.length) speech.prefetch(segments, { skipTranslation: getUserData().bilingualMode });
}

const ModularMultipleChoice = ({
  speech,
  options,
  videoURL,
  imageURL,
  imageInPrompt,
  questionText,
  readOptionsAloud,
  verticalOptions,
  isMarkupInstruction,
  locale,
  isInstruction,
  isRepeating,
  onDone,
  shouldExtendTimeout,
  timeoutExtensionMultiplier,
  idleTimeoutOverrides,
  onImageLoadError,
  onAllImagesFailed,
  idleEncouragementText,
  instructionRepeatable,
  instructionRepeatVariation,
  repeatInstructions,
  handleRepeatInstructions,
  beginOverlayRepeatPlayback,
  endOverlayRepeatPlayback,
  setHideParentRepeatButton,
  currentTriggerAction,
  currentTriggerData,
  onTriggerActionComplete,
}) => {
  const [inFTUE, setInFTUE] = useState(false);
  const inFTUERef = useRef(inFTUE);
  inFTUERef.current = inFTUE;
  const containerRef = useRef(null);
  const containerHeightPx = useContainerHeightVar(containerRef);
  const effectiveOptions = options || [];

  const hasOptions = effectiveOptions.length > 0;
  const isImageBased = effectiveOptions.some(o => o.imageUrl);
  const isVerticalText = verticalOptions || false;
  const hasVideo = !!videoURL;
  // Vocabulary clue images render inside the prompt bar (the definition slot),
  // not the media section below it. Video (if any) always owns the media section.
  const promptImageURL = imageInPrompt && !hasVideo ? imageURL : null;
  const hasImage = !!imageURL && !promptImageURL;

  // State — visible by default. Only what's provided renders (JSX guards on data).
  // The [start] trigger hides everything for FTUE modeling.
  const [selectedOption, setSelectedOption] = useState(null);
  const [hidePrompt, setHidePrompt] = useState(false);
  const [hideMedia, setHideMedia] = useState(false);
  const [hideOptions, setHideOptions] = useState(false);
  const [hideSubmit, setHideSubmit] = useState(false);
  const [hideRepeat, setHideRepeat] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [canSelectOption, setCanSelectOption] = useState(false);
  const [animatedOptions, setAnimatedOptions] = useState([]);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submittedRef = useRef(false);
  /** Synchronous guard: state submitted can lag; blocks double onDone if handleSubmit runs twice before re-render. */
  const submissionDispatchedRef = useRef(false);
  const [highlightedOption, setHighlightedOption] = useState(null);
  const [pointerTarget, setPointerTarget] = useState(null);
  const [showRepeatOverlay, setShowRepeatOverlay] = useState(false);
  /** After [show:repeatOverlayInput]; false during TTS between overlay visible and input gate. */
  const [repeatOverlayInputEnabled, setRepeatOverlayInputEnabled] = useState(false);
  /** True from Replay click until overlay TTS onEnd/onError — syncs with Reader isRepeating and blocks Next/Replay. */
  const [repeatOverlayPlaybackBusy, setRepeatOverlayPlaybackBusy] = useState(false);

  /** FTUE: only the static image should be prominent (prompt/options/submit/repeat hidden, media visible). */
  const imageOnlyLayout =
    hasImage &&
    !hasVideo &&
    !hideMedia &&
    hidePrompt &&
    hideOptions &&
    hideSubmit &&
    hideRepeat;

  const isMountedRef = useIsMounted();
  const prevTriggerRef = useRef(null);
  const prevTriggerDataRef = useRef(null);
  const triggerVideoResolverRef = useRef(null);
  /** Synchronous storage for playVideo() promise resolve — avoids race if onDone fires before state commit */
  const videoPromiseResolverRef = useRef(null);
  /** Invalidate in-flight FTUE option stagger if hide:options or a new show:options runs */
  const ftueOptionsRevealGenerationRef = useRef(0);
  /** Invalidate in-flight playInstructions option stagger / speech if unmount or superseded */
  const playInstructionsGenerationRef = useRef(0);
  /** Prevents overlapping selection TTS (second tap while first speakWithPromise runs). */
  const selectionSpeechInFlightRef = useRef(false);
  /** True while [show:repeatOverlayInput] is blocking Phrase until Next (or hide/unmount cleanup). */
  const repeatOverlayPendingRef = useRef(false);
  /** In-flight Replay tap — blocks double-starts before state commits. */
  const repeatOverlayReplayInFlightRef = useRef(false);
  /** Reader beginOverlayRepeatPlayback ran — must call endOverlayRepeatPlayback in finishReplay or unmount. */
  const overlayRepeatTimersActiveRef = useRef(false);
  /** Bumped on each syllable-replay start so an in-progress playMarkupSequence loop
   *  can bail (stop issuing further tokens) on unmount or a newer replay. */
  const syllableReplayGenerationRef = useRef(0);
  /** Populated (below) once playMarkupSequence is defined, so the idle-nudge path can
   *  play syllables from mp3 even though useIdleTimer is created above that definition. */
  const speakSyllableEncouragementRef = useRef(null);
  const endOverlayRepeatPlaybackRef = useRef(endOverlayRepeatPlayback);
  endOverlayRepeatPlaybackRef.current = endOverlayRepeatPlayback;

  /** Reader passes `setHideRepeatButton.bind(this)` — new identity every render; do not list in effect deps. */
  const setHideParentRepeatButtonRef = useRef(setHideParentRepeatButton);
  setHideParentRepeatButtonRef.current = setHideParentRepeatButton;

  const onTriggerActionCompleteRef = useRef(onTriggerActionComplete);
  onTriggerActionCompleteRef.current = onTriggerActionComplete;

  useEffect(() => {
    submittedRef.current = submitted;
    if (!submitted) {
      submissionDispatchedRef.current = false;
    }
  }, [submitted]);

  const selectedOptionRef = useRef(null);
  useEffect(() => {
    selectedOptionRef.current = selectedOption;
  }, [selectedOption]);

  const effectiveOptionsRef = useRef(effectiveOptions);
  effectiveOptionsRef.current = effectiveOptions;

  // Match Spelling: Reader.getRepeatInstructions() → repeatInstructions (repeatVariation or short prompt); idle uses same copy when meta is flattened.
  const encouragementText = idleEncouragementText || repeatInstructions;
  const timeoutMessage = COPY_STRINGS.lets_move_on;

  // ASSMNT-2005: per-testlet-type idle/full timeouts (idleTimeoutOverrides, resolved in Phrase.js
  // from testlet.type, same for every language); testlet types with no override fall back below.
  const { idleEncouragementTimeout: extendedIdleEncouragementTimeout, idleSubmitTimeout: extendedSubmitTimeout } = resolveIdleTimeouts(
    idleTimeoutOverrides,
    { idleEncouragementTimeout: IDLE_ENCOURAGEMENT_TIMEOUT, idleSubmitTimeout: IDLE_SUBMIT_TIMEOUT },
    shouldExtendTimeout,
    timeoutExtensionMultiplier
  );

  const { resetIdleTimer, clearIdleTimer, idleSpeechPlaying, idleTimeoutExpired } = useIdleTimer(
    speech,
    extendedIdleEncouragementTimeout,
    extendedSubmitTimeout,
    // Evaluar syllable blending: voice the idle nudge from pre-recorded syllable mp3
    // (via ref — playMarkupSequence is defined below) instead of TTS-ing "ce, ro".
    isMarkupInstruction ? (text) => speakSyllableEncouragementRef.current?.(text) : undefined
  );

  const { repeat: repeatIconSize, submit: submitIconSize } = useMmcResponsiveIconButtonProps();

  /** Reader sets isRepeating while repeat audio plays (same as Spelling); block input like instruction phase. */
  const interactionLocked = isInstruction || isRepeating;

  const unmountVideoWhenDone = useMemo(() => getUserData().sessionData.isiPad, []);

  const repeatVariationTrimmed = String(instructionRepeatVariation || '').trim();
  const suppressVideoReplayOverlay =
    (isInstruction && instructionRepeatable === true && repeatVariationTrimmed !== '') ||
    showRepeatOverlay ||
    repeatOverlayPlaybackBusy;

  // --- Media helpers ---

  const playVideo = useCallback(async () => {
    LOG('playVideo() — creating promise, setting videoPlaying=true');
    const videoPromise = new Promise((resolve) => {
      videoPromiseResolverRef.current = resolve;
    });
    setVideoPlaying(true);
    await videoPromise;
    if (!isMountedRef.current) return;
    LOG('playVideo() — video promise resolved, setting videoPlaying=false');
    setVideoPlaying(false);
    videoPromiseResolverRef.current = null;
  }, []);

  // --- Trigger handling (action:target system) ---

  const showTargets = useCallback((targets) => {
    const ftueOptionsInBatch = inFTUERef.current && targets.includes('options');
    let pendingSyncComplete = false;
    // When batch is [show:options,submit], submit's mark is skipped on purpose: the async option
    // stagger owns onTriggerActionComplete: submit is not separately awaitable in that case.
    const markPendingSyncComplete = () => {
      if (!ftueOptionsInBatch) {
        pendingSyncComplete = true;
      }
    };

    for (const t of targets) {
      switch (t) {
        case 'repeat': setHideRepeat(false); break;
        case 'prompt': setHidePrompt(false); setHideRepeat(false); break;
        case 'options':
          setHideOptions(false);
          // Use ref so [show:options] sees FTUE immediately after [start] (state may not have re-rendered yet).
          if (inFTUERef.current) {
            ftueOptionsRevealGenerationRef.current += 1;
            const gen = ftueOptionsRevealGenerationRef.current;
            setAnimatedOptions([]);
            (async () => {
              try {
                if (readOptionsAloud) {
                  prefetchReadOptionsAloud(speech, effectiveOptions);
                }
                for (const option of effectiveOptions) {
                  // Match playInstructions: delay, reveal tile, then speak label when readOptionsAloud
                  await new Promise((resolve) => {
                    setTimeout(() => {
                      if (!isMountedRef.current || ftueOptionsRevealGenerationRef.current !== gen) {
                        resolve();
                        return;
                      }
                      const label = option.text || option.key;
                      setAnimatedOptions((prev) => [...prev, option.key]);
                      if (readOptionsAloud) {
                        speech.speakWithPromise({
                          textToSpeak: `"${label}".`,
                          skipTranslation: getUserData().bilingualMode,
                          useGenerative: true,
                        }).finally(resolve);
                      } else {
                        resolve();
                      }
                    }, TIME_BETWEEN_OPTIONS);
                  });
                  if (!isMountedRef.current || ftueOptionsRevealGenerationRef.current !== gen) {
                    break;
                  }
                }
              } finally {
                // One completion per show batch; skip if [show:repeatOverlayInput] is waiting for Next
                if (
                  isMountedRef.current &&
                  !repeatOverlayPendingRef.current
                ) {
                  onTriggerActionCompleteRef.current?.();
                }
              }
            })();
          } else {
            setAnimatedOptions(effectiveOptions.map((o) => o.key));
            pendingSyncComplete = true;
          }
          break;
        case 'video': setHideMedia(false); break;
        case 'image': setHideMedia(false); break;
        case 'repeatOverlay': {
          const trimmed = String(instructionRepeatVariation || '').trim();
          if (instructionRepeatable !== true || trimmed === '') {
            markPendingSyncComplete();
          } else {
            setRepeatOverlayInputEnabled(false);
            setShowRepeatOverlay(true);
            markPendingSyncComplete();
          }
          break;
        }
        case 'repeatOverlayInput': {
          const trimmed = String(instructionRepeatVariation || '').trim();
          if (instructionRepeatable !== true || trimmed === '') {
            markPendingSyncComplete();
          } else {
            setPointerTarget(null);
            setShowRepeatOverlay(true);
            setRepeatOverlayInputEnabled(true);
            repeatOverlayPendingRef.current = true;
          }
          break;
        }
        case 'submit': setHideSubmit(false); break;
        default: LOG(`  show: unknown target "${t}"`); break;
      }
    }

    if (pendingSyncComplete && !repeatOverlayPendingRef.current) {
      queueMicrotask(() => onTriggerActionCompleteRef.current?.());
    }
  }, [effectiveOptions, readOptionsAloud, speech, instructionRepeatable, instructionRepeatVariation]);

  const hideTargets = useCallback((targets) => {
    for (const t of targets) {
      switch (t) {
        case 'repeat': setHideRepeat(true); break;
        case 'prompt':
          setHidePrompt(true);
          setHideRepeat(true);
          break;
        case 'options':
          ftueOptionsRevealGenerationRef.current += 1;
          setHideOptions(true);
          setAnimatedOptions([]);
          setSelectedOption(null);
          setHighlightedOption(null);
          break;
        case 'video': setHideMedia(true); break;
        case 'image': setHideMedia(true); break;
        case 'repeatOverlay':
          setShowRepeatOverlay(false);
          setRepeatOverlayInputEnabled(false);
          if (repeatOverlayPendingRef.current) {
            repeatOverlayPendingRef.current = false;
            queueMicrotask(() => onTriggerActionCompleteRef.current?.());
          }
          break;
        case 'submit': setHideSubmit(true); break;
        default: LOG(`  hide: unknown target "${t}"`); break;
      }
    }
  }, []);

  // Highlight is non-awaitable: pointer shows immediately, speech continues,
  // pointer stays visible until the next trigger clears it.
  const highlightTargets = useCallback((targets) => {
    const target = targets[0];
    if (!target) {
      LOG('  highlight: (empty — clear pointer)');
      setPointerTarget(null);
      setHighlightedOption(null);
      return;
    }

    if (target === 'repeatOverlayReplay' || target === 'repeatOverlayNext') {
      LOG(`  highlight: repeat overlay button "${target}"`);
      setHighlightedOption(null);
      setPointerTarget(target);
      return;
    }

    if (target === 'backspace') {
      LOG('  highlight: backspace — MMC has no backspace; clear pointer (misrouted trigger)');
      setPointerTarget(null);
      setHighlightedOption(null);
      return;
    }

    if (UI_ELEMENTS.has(target)) {
      LOG(`  highlight: pointing at UI element "${target}"`);
      setPointerTarget(target);
    } else {
      LOG(`  highlight: highlighting option "${target}"`);
      setHighlightedOption(target);
    }
  }, []);

  const clickTargets = useCallback((targets) => {
    const target = targets[0];
    if (!target) return;

    if (target === 'backspace') {
      LOG('  click: backspace — MMC has no backspace; completing trigger (no-op)');
      queueMicrotask(() => onTriggerActionCompleteRef.current?.());
      return;
    }

    if (target === 'video') {
      LOG('  click:video — playing video (awaitable)');
      setHideMedia(false);
      setPointerTarget('video');
      setVideoPlaying(true);
      triggerVideoResolverRef.current = () => {
        LOG('  click:video resolver — video ended');
        setVideoPlaying(false);
        setPointerTarget(null);
        triggerVideoResolverRef.current = null;
        onTriggerActionCompleteRef.current?.();
      };
    } else if (UI_ELEMENTS.has(target)) {
      LOG(`  click: pointing at UI element "${target}"`);
      setPointerTarget(target);
      setTimeout(() => {
        if (!isMountedRef.current) return;
        setPointerTarget(null);
        onTriggerActionCompleteRef.current?.();
      }, HIGHLIGHT_DURATION);
    } else {
      // Target is an option key — highlight it with pointer
      LOG(`  click: highlighting option "${target}" with pointer`);
      setHighlightedOption(target);
      setPointerTarget('option');
      setTimeout(() => {
        if (!isMountedRef.current) return;
        setHighlightedOption(null);
        setPointerTarget(null);
        onTriggerActionCompleteRef.current?.();
      }, HIGHLIGHT_DURATION);
    }
  }, []);

  // Evaluar markup-instruction replay (blending + GPC): voice the repeatVariation from
  // pre-recorded audio via the shared playMarkupSequence (services/playMarkupSequence.js),
  // matching the initial play path, instead of sending raw <syllable>/<phoneme> markup to
  // 11Labs (which cannot voice the tags -> silence). isMountedRef/generation become the
  // caller-supplied isStale() check so the same player is reused by Phrase, MMC, and Reader.
  // Content locale (from the story's tags, via the `locale` prop) selects es-mx vs en-us assets.
  const speakMarkupSequence = useCallback((text, gen) => (
    playMarkupSequence(speech, text, {
      isStale: () => !isMountedRef.current || syllableReplayGenerationRef.current !== gen,
      locale,
    })
  ), [speech, locale]);

  // Wire the idle-nudge playback (its ref is declared above, before playMarkupSequence
  // exists) to the same pre-recorded sequence the repeat button uses. Assigning a ref
  // during render mirrors endOverlayRepeatPlaybackRef above; returns the promise so the
  // idle timer awaits the full sequence.
  speakSyllableEncouragementRef.current = (text) => {
    syllableReplayGenerationRef.current += 1;
    const gen = syllableReplayGenerationRef.current;
    return speakMarkupSequence(String(text), gen);
  };

  const handleRepeatOverlayReplay = useCallback(() => {
    const text = String(instructionRepeatVariation || '').trim();
    if (
      !text ||
      !repeatOverlayInputEnabled ||
      repeatOverlayReplayInFlightRef.current ||
      repeatOverlayPlaybackBusy ||
      isRepeating
    ) {
      return;
    }
    repeatOverlayReplayInFlightRef.current = true;
    setRepeatOverlayPlaybackBusy(true);
    setShowRepeatOverlay(false);

    if (beginOverlayRepeatPlayback) {
      if (!beginOverlayRepeatPlayback()) {
        repeatOverlayReplayInFlightRef.current = false;
        setRepeatOverlayPlaybackBusy(false);
        if (isInstruction && instructionRepeatable === true && repeatOverlayPendingRef.current) {
          setShowRepeatOverlay(true);
        }
        return;
      }
      overlayRepeatTimersActiveRef.current = true;
    }

    const finishReplay = () => {
      if (!isMountedRef.current) return;
      repeatOverlayReplayInFlightRef.current = false;
      setRepeatOverlayPlaybackBusy(false);
      if (overlayRepeatTimersActiveRef.current) {
        overlayRepeatTimersActiveRef.current = false;
        endOverlayRepeatPlayback?.();
      }
      if (isInstruction && instructionRepeatable === true && repeatOverlayPendingRef.current) {
        setShowRepeatOverlay(true);
      }
    };

    const segments = splitRepeatVariationOnSplitMarkers(text);
    if (segments.length === 0) {
      finishReplay();
      return;
    }

    // Evaluar markup instruction (blending + GPC): play pre-recorded audio for the
    // repeatVariation instead of sending raw <syllable>/<phoneme> markup to TTS (11Labs),
    // matching the initial instruction play. Pass the raw text — playMarkupSequence parses
    // the markup itself (GPC) or falls back to the bare syllable list (blending). Bilingual
    // mode is fine: language-neutral sounds are marked skipTranslation, prose still translates.
    if (isMarkupInstruction) {
      syllableReplayGenerationRef.current += 1;
      const gen = syllableReplayGenerationRef.current;
      speakMarkupSequence(text, gen)
        .then(finishReplay)
        .catch((err) => {
          // Recover overlay state, but surface the failure — a rejected replay
          // means the student heard nothing on tap, which is worth logging.
          logSessionInfo('mmc_syllable_replay_failed', { message: err?.message });
          finishReplay();
        });
      return;
    }

    const sequenceBase = {
      passThroughSpeechTags: true,
      useGenerative: true,
      onEnd: finishReplay,
      onError: finishReplay,
    };

    if (getUserData().bilingualMode) {
      const flat = segments
        .flatMap((seg) => parseSkipTranslationTags(seg))
        .filter((item) => item && String(item.textToSpeak || '').trim());
      if (!flat.length) {
        finishReplay();
        return;
      }
      // Prefetch parsed segments with correct skipTranslation per item for cache alignment
      if (speech.prefetch) {
        flat.forEach((item) => {
          speech.prefetch([item.textToSpeak], { skipTranslation: item.skipTranslation });
        });
      }
      speech.speakSequence({
        textToSpeak: flat,
        passThroughSpeechTags: true,
        onEnd: finishReplay,
        onError: finishReplay,
      });
    } else {
      // Non-bilingual: prefetch all segments (no skipTranslation, uses default locale)
      if (speech.prefetch) {
        speech.prefetch(segments);
      }
      if (segments.length === 1) {
        speech.speak(
          {
            textToSpeak: segments[0],
            passThroughSpeechTags: true,
            useGenerative: true,
            onEnd: finishReplay,
            onError: finishReplay,
          },
          false,
        );
      } else {
        speech.speakSequence({
          ...sequenceBase,
          textToSpeak: segments.map((s) => ({ textToSpeak: s })),
        });
      }
    }
  }, [
    speech,
    instructionRepeatVariation,
    isInstruction,
    instructionRepeatable,
    isRepeating,
    repeatOverlayPlaybackBusy,
    beginOverlayRepeatPlayback,
    endOverlayRepeatPlayback,
    repeatOverlayInputEnabled,
    isMarkupInstruction,
    // Depend on the memoized speakMarkupSequence (not the stable import): it closes over
    // `locale`, so a content-locale change must rebuild this callback or overlay Replay
    // would resolve assets against a stale locale.
    speakMarkupSequence,
  ]);

  const handleRepeatOverlayNext = useCallback(() => {
    if (
      !repeatOverlayInputEnabled ||
      repeatOverlayReplayInFlightRef.current ||
      isRepeating ||
      repeatOverlayPlaybackBusy
    ) {
      return;
    }
    repeatOverlayPendingRef.current = false;
    setRepeatOverlayInputEnabled(false);
    setShowRepeatOverlay(false);
    onTriggerActionCompleteRef.current?.();
  }, [
    isRepeating,
    repeatOverlayPlaybackBusy,
    repeatOverlayInputEnabled,
  ]);

  const handleTrigger = useCallback((action, data) => {
    LOG(`handleTrigger("${action}", [${data.join(',')}])`);

    // Clear pointer from any previous highlight — each trigger resets it.
    // Highlight will re-set it; click sets its own pointer during animation.
    setPointerTarget(null);
    setHighlightedOption(null);

    switch (action) {
      case TriggerActions.Start:
        LOG('  → Start: hiding everything for FTUE');
        clearIdleTimer();
        setHidePrompt(true);
        setHideMedia(true);
        setHideOptions(true);
        setHideSubmit(true);
        setHideRepeat(true);
        setCanSelectOption(false);
        setAnimatedOptions([]);
        inFTUERef.current = true;
        setInFTUE(true);
        setShowRepeatOverlay(false);
        setRepeatOverlayInputEnabled(false);
        repeatOverlayPendingRef.current = false;
        break;
      case TriggerActions.Ready:
        LOG('  → Ready: FTUE complete');
        inFTUERef.current = false;
        setInFTUE(false);
        setShowRepeatOverlay(false);
        setRepeatOverlayInputEnabled(false);
        repeatOverlayPendingRef.current = false;
        break;
      case TriggerActions.Show:
        showTargets(data);
        break;
      case TriggerActions.Hide:
        hideTargets(data);
        break;
      case TriggerActions.Highlight:
        highlightTargets(data);
        break;
      case TriggerActions.Click:
        clickTargets(data);
        break;
      default:
        LOG(`  → Unknown trigger action: "${action}"`);
        break;
    }
  }, [showTargets, hideTargets, highlightTargets, clickTargets, clearIdleTimer]);

  const handleTriggerRef = useRef(handleTrigger);
  handleTriggerRef.current = handleTrigger;

  useEffect(() => {
    if (!currentTriggerAction) {
      prevTriggerRef.current = null;
      prevTriggerDataRef.current = null;
      return;
    }
    const data = currentTriggerData || [];
    const sameAction = currentTriggerAction === prevTriggerRef.current;
    const sameData = triggerDataShallowEqual(data, prevTriggerDataRef.current);
    if (sameAction && sameData) {
      return;
    }
    LOG(`Trigger effect — action: "${currentTriggerAction}", data: [${data.join(',')}]`);
    prevTriggerRef.current = currentTriggerAction;
    prevTriggerDataRef.current = [...data];
    handleTriggerRef.current(currentTriggerAction, data);
  }, [currentTriggerAction, currentTriggerData]);

  // --- Flow ---

  const startInteraction = useCallback(() => {
    LOG('startInteraction() — canSelectOption=true, starting idle timer');
    setCanSelectOption(true);
    setHideSubmit(false);
    if (selectedOptionRef.current) {
      setSubmitEnabled(true);
    }
    resetIdleTimer(encouragementText);
  }, [encouragementText, resetIdleTimer]);

  const playInstructions = useCallback(async () => {
    playInstructionsGenerationRef.current += 1;
    const gen = playInstructionsGenerationRef.current;
    LOG('playInstructions() — normal item flow', { hasVideo, readOptionsAloud });
    setCanSelectOption(false);
    setSelectedOption(null);
    setSubmitEnabled(false);
    clearIdleTimer();

    if (effectiveOptions.length > 0) {
      if (readOptionsAloud) {
        LOG('  Reading through', effectiveOptions.length, 'options aloud…');
        prefetchReadOptionsAloud(speech, effectiveOptions);
        setAnimatedOptions([]);
        for (const option of effectiveOptions) {
          await new Promise((resolve) => {
            setTimeout(() => {
              if (!isMountedRef.current || playInstructionsGenerationRef.current !== gen) {
                resolve();
                return;
              }
              const label = option.text || option.key;
              LOG(`    Speaking option: "${label}"`);
              setAnimatedOptions((prev) => [...prev, option.key]);
              speech.speakWithPromise({
                textToSpeak: `"${label}".`,
                skipTranslation: getUserData().bilingualMode,
                useGenerative: true,
              }).finally(resolve);
            }, TIME_BETWEEN_OPTIONS);
          });
          if (!isMountedRef.current || playInstructionsGenerationRef.current !== gen) {
            break;
          }
        }
        LOG('  Finished reading options');
      } else {
        LOG('  Showing all options immediately');
        if (!isMountedRef.current || playInstructionsGenerationRef.current !== gen) {
          return;
        }
        setAnimatedOptions(effectiveOptions.map(o => o.key));
      }
    }

    if (!isMountedRef.current || playInstructionsGenerationRef.current !== gen) {
      return;
    }

    if (hasVideo) {
      LOG('  Playing video…');
      await playVideo();
      LOG('  Video done');
    }

    if (!isMountedRef.current || playInstructionsGenerationRef.current !== gen) {
      return;
    }
    LOG('playInstructions() — done, starting interaction');
    startInteraction();
  }, [
    hasVideo,
    readOptionsAloud,
    effectiveOptions,
    speech,
    clearIdleTimer,
    startInteraction,
    playVideo,
  ]);

  // --- Handlers ---

  const handleOptionSelection = useCallback(
    async (key) => {
      if (isRepeating) {
        return;
      }
      if (selectionSpeechInFlightRef.current) {
        LOG(`handleOptionSelection("${key}") skipped — selection speech in flight`);
        return;
      }
      LOG(`handleOptionSelection("${key}")`);
      selectionSpeechInFlightRef.current = true;
      setCanSelectOption(false);
      setSubmitEnabled(false);
      clearIdleTimer();
      setSelectedOption(key);

      try {
        // readOptionsAloud governs ALL option TTS — instruction read-through, FTUE reveal, and this
        // selection read. When false, options are never spoken aloud; we still re-enable input below.
        if (readOptionsAloud) {
          const selectedOpt = effectiveOptions.find((o) => o.key === key);
          const label = selectedOpt?.text || key;
          LOG(`  Speaking selection: "${label}"`);
          await speech.speakWithPromise({
            textToSpeak: `"${label}".`,
            skipTranslation: getUserData().bilingualMode,
            useGenerative: true,
          });
        }

        if (!isMountedRef.current) return;
        if (submittedRef.current) return;
        LOG('  Selection done — re-enabling input');
        setCanSelectOption(true);
        setSubmitEnabled(true);
        resetIdleTimer(encouragementText);
      } finally {
        selectionSpeechInFlightRef.current = false;
      }
    },
    [isRepeating, readOptionsAloud, effectiveOptions, speech, clearIdleTimer, encouragementText, resetIdleTimer],
  );

  const handleSubmit = useCallback(() => {
    if (submissionDispatchedRef.current) {
      LOG('handleSubmit() — skipped, submission already dispatched');
      return;
    }
    submissionDispatchedRef.current = true;
    const raw = selectedOptionRef.current;
    LOG(`handleSubmit() — selectedOption="${raw}"`);
    clearIdleTimer();
    setSubmitted(true);
    setSubmitEnabled(false);
    setCanSelectOption(false);
    const response = raw == null || raw === '' ? NO_RESPONSE : raw;
    if (onDone) onDone(response);
  }, [onDone, clearIdleTimer]);

  const playInstructionsRef = useRef(playInstructions);
  playInstructionsRef.current = playInstructions;
  const startInteractionRef = useRef(startInteraction);
  startInteractionRef.current = startInteraction;
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const speechRef = useRef(speech);
  speechRef.current = speech;
  const timeoutMessageRef = useRef(timeoutMessage);
  timeoutMessageRef.current = timeoutMessage;

  // --- Effects ---

  useEffect(() => {
    setHideParentRepeatButtonRef.current?.(true);
    return () => {
      setHideParentRepeatButtonRef.current?.(false);
    };
  }, []);

  // Unmount: unblock awaitable triggers (click:video, show:repeatOverlayInput) — at most one callback;
  // resolve playVideo() promise so playInstructions is not stuck if VideoPlayer never calls onDone.
  useEffect(() => {
    return () => {
      if (overlayRepeatTimersActiveRef.current) {
        overlayRepeatTimersActiveRef.current = false;
        endOverlayRepeatPlaybackRef.current?.();
      }
      const resolveVideoPromise = videoPromiseResolverRef.current;
      if (resolveVideoPromise) {
        videoPromiseResolverRef.current = null;
        resolveVideoPromise();
      }
      let needComplete = false;
      if (triggerVideoResolverRef.current) {
        triggerVideoResolverRef.current = null;
        needComplete = true;
      }
      if (repeatOverlayPendingRef.current) {
        repeatOverlayPendingRef.current = false;
        needComplete = true;
      }
      if (needComplete) {
        onTriggerActionCompleteRef.current?.();
      }
    };
  }, []);

  // Idle encouragement speech vs input: selection read from ref — do not list selectedOption in deps (re-ran
  // this effect on every selection change and could set canSelectOption true mid handleOptionSelection TTS).
  useEffect(() => {
    if (idleSpeechPlaying === undefined) return;
    LOG('Idle speech playing changed:', idleSpeechPlaying);
    setCanSelectOption(!idleSpeechPlaying);
    if (idleSpeechPlaying) {
      setSubmitEnabled(false);
    } else if (selectedOptionRef.current) {
      setSubmitEnabled(true);
    }
  }, [idleSpeechPlaying]); // eslint-disable-line react-hooks/exhaustive-deps -- selectedOptionRef only; see comment above effect

  // Idle auto-submit: setSubmitted(true) before TTS locks the UI while "let's move on" plays; onDone runs
  // only once — inside handleSubmit() after that promise (not a double-submit). handleSubmit repeats
  // setSubmitted(true) for a single code path. If we unmount before the .then, onDone may not run;
  // accepted edge case unless product wants synchronous onDone.
  useEffect(() => {
    if (!idleTimeoutExpired) return;
    if (isInstruction || isRepeating || inFTUE || submitted) {
      LOG('Idle timeout ignored — instruction/repeat/FTUE or already submitted');
      return;
    }
    LOG('Idle timeout expired — auto-submitting');
    setSubmitted(true);
    setCanSelectOption(false);
    setSubmitEnabled(false);

    speechRef.current
      .speakWithPromise({
        textToSpeak: timeoutMessageRef.current,
      })
      .then(() => {
        if (!isMountedRef.current) return;
        handleSubmitRef.current();
      })
      .catch((err) => {
        logSessionInfo('mmc_idle_timeout_speech_failed', { message: err?.message });
        if (!isMountedRef.current) return;
        handleSubmitRef.current();
      });
  }, [idleTimeoutExpired, isInstruction, isRepeating, inFTUE, submitted]);

  useEffect(() => {
    LOG(`isInstruction changed: ${isInstruction}`);
    if (!isInstruction) {
      // FTUE: testlet script may have left prompt/media/repeat hidden if it omitted [show:…] for those targets.
      // Reset chrome for the real item (same baseline as non-FTUE) and reveal option tiles — they stay opacity:0
      // until animatedOptions includes their keys.
      if (inFTUERef.current) {
        LOG('  Trigger-driven flow complete — starting interaction');
        inFTUERef.current = false;
        setInFTUE(false);
        setHidePrompt(false);
        setHideMedia(false);
        setHideOptions(false);
        setHideRepeat(false);
        setSelectedOption(null);
        selectedOptionRef.current = null;
        const opts = effectiveOptionsRef.current;
        if (opts.length > 0) {
          setAnimatedOptions(opts.map((o) => o.key));
        }
        startInteractionRef.current();
      } else {
        setHidePrompt(false);
        setHideMedia(false);
        setHideOptions(false);
        setHideRepeat(false);
        inFTUERef.current = false;
        setInFTUE(false);
        LOG('  Calling playInstructions()');
        playInstructionsRef.current();
      }
    }
  }, [isInstruction]);

  // --- Render ---

  return (
    <div
      ref={containerRef}
      className={cx('modularMultipleChoice', {
        'modularMultipleChoice--imageOnly': imageOnlyLayout,
      })}
    >
      <MmcPromptBar
        hidePrompt={hidePrompt}
        hideRepeat={hideRepeat}
        questionText={questionText}
        imageURL={promptImageURL}
        hideImage={hideMedia}
        containerHeightPx={containerHeightPx}
        repeatIconSize={repeatIconSize}
        onRepeatClick={() => {
          if (!interactionLocked && canSelectOption && handleRepeatInstructions) {
            clearIdleTimer();
            handleRepeatInstructions();
          }
        }}
        repeatDisabled={interactionLocked || !canSelectOption}
        showRepeatPointer={pointerTarget === 'repeat'}
      />

      <MmcMediaSection
        hasVideo={hasVideo}
        hasImage={hasImage}
        hideMedia={hideMedia}
        videoURL={videoURL}
        imageURL={imageURL}
        speech={speech}
        videoPlaying={videoPlaying}
        submitted={submitted}
        unmountVideoWhenDone={unmountVideoWhenDone}
        suppressVideoReplayOverlay={suppressVideoReplayOverlay}
        showRepeatOverlay={showRepeatOverlay}
        pointerTarget={pointerTarget}
        onRepeatOverlayReplay={handleRepeatOverlayReplay}
        onRepeatOverlayNext={handleRepeatOverlayNext}
        onVideoStart={() => {
          LOG('VideoPlayer onStart');
          clearIdleTimer();
          setCanSelectOption(false);
          setSubmitEnabled(false);
        }}
        onVideoDone={() => {
          if (!isMountedRef.current) return;
          LOG('VideoPlayer onDone — triggerResolver:', !!triggerVideoResolverRef.current, 'promiseResolver:', !!videoPromiseResolverRef.current);
          if (triggerVideoResolverRef.current) {
            triggerVideoResolverRef.current();
            // [click:video] and playInstructions() both use the same VideoPlayer onDone; if playVideo()'s
            // promise is still pending, resolve it so playInstructions does not hang (Bugbot / race).
            const resolvePlayInstructions = videoPromiseResolverRef.current;
            if (resolvePlayInstructions) {
              videoPromiseResolverRef.current = null;
              resolvePlayInstructions();
            }
            return;
          }
          const resolveVideo = videoPromiseResolverRef.current;
          if (resolveVideo) {
            videoPromiseResolverRef.current = null;
            resolveVideo();
          }
          if (submittedRef.current) return;
          setCanSelectOption(true);
          if (selectedOptionRef.current) {
            setSubmitEnabled(true);
          }
          resetIdleTimer(encouragementText);
        }}
      />

      <MmcOptionsSection
        hasOptions={hasOptions}
        hideOptions={hideOptions}
        isImageBased={isImageBased}
        isVerticalText={isVerticalText}
        effectiveOptions={effectiveOptions}
        animatedOptions={animatedOptions}
        selectedOption={selectedOption}
        highlightedOption={highlightedOption}
        canSelectOption={canSelectOption}
        isRepeating={isRepeating}
        onOptionSelect={handleOptionSelection}
        onImageLoadError={onImageLoadError}
        onAllImagesFailed={onAllImagesFailed}
      />

      <MmcSubmitButton
        submitIconSize={submitIconSize}
        hideSubmit={hideSubmit}
        pointerTarget={pointerTarget}
        isInstruction={isInstruction}
        isRepeating={isRepeating}
        selectedOption={selectedOption}
        submitEnabled={submitEnabled}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default ModularMultipleChoice;
